import { useEffect, useState, useRef } from 'react';
import { AGENTS, FETCH_TIMEOUT_MS, POLL_INTERVAL_MS } from '../lib/agents';
import { AgentStateSchema, offlineState, type AgentState } from '../lib/schema';

const INITIAL: AgentState[] = AGENTS.map((a) => offlineState(a.id));

async function fetchOne(url: string, agentId: AgentState['agentId']): Promise<AgentState> {
  try {
    const res = await fetch(url, {
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
      headers: { accept: 'application/json' },
    });
    if (res.status === 304) throw new Error('not-modified');
    if (!res.ok) throw new Error(`http-${res.status}`);
    const json = (await res.json()) as unknown;
    return AgentStateSchema.parse(json);
  } catch {
    return offlineState(agentId);
  }
}

export function useAgentStates(): { states: AgentState[]; lastSync: number | null } {
  const [states, setStates] = useState<AgentState[]>(INITIAL);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    let timer: number | undefined;

    const tick = async () => {
      const next = await Promise.all(
        AGENTS.map((a) => fetchOne(a.statusUrl, a.id)),
      );
      if (!mountedRef.current) return;
      setStates((prev) =>
        next.map((n, i) =>
          n.status === 'offline' && prev[i].status !== 'offline' ? prev[i] : n,
        ),
      );
      setLastSync(Date.now());
    };

    const schedule = () => {
      const interval = document.hidden
        ? POLL_INTERVAL_MS.hidden
        : POLL_INTERVAL_MS.visible;
      timer = window.setTimeout(async () => {
        await tick();
        schedule();
      }, interval);
    };

    void tick();
    schedule();

    const onVisibility = () => {
      if (timer) window.clearTimeout(timer);
      void tick();
      schedule();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      mountedRef.current = false;
      if (timer) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return { states, lastSync };
}
